import { requestJSON, requestVoid } from "./http";
import type { AIGovernancePolicy, AIGovernanceWorkload, AttentionItem, AutomationPolicyPage, AuthorityResolution, CaptureRequest, EvidenceRequest, EvidenceReviewSubmission, EvidenceSource, IntegrityFinding, MatterAggregate, PolicySummary, ProgramAggregate, Readiness, ResponseHistoryPage, WorkflowTask } from "./types";
import type { MatterSummary, ProgramSummary, SummaryPage, SummaryQuery } from "./summaryTypes";
import type { BackgroundJobSnapshot, JobRecoveryReceipt, ProjectionHealth, ReconcileResult } from "./operationsTypes";
import type { BankJourneysResponse } from "./verticalTypes";
import { normalizeProgramAggregate } from "./programAggregate";

const apiBase = import.meta.env.VITE_API_BASE_URL ?? "";

export type DepartmentGrant = {
  path: string[];
  role_codes?: string[];
  permission_codes?: string[];
};

export type ScopeNode = {
  id: string;
  code?: string;
  name: string;
  kind: "ORGANIZATION" | "LEGAL_ENTITY" | "ORGANIZATION_UNIT" | "BRANCH" | "DEPARTMENT" | "FUNCTION" | "BUSINESS_UNIT" | "CRITICAL_SERVICE";
  parent_id?: string;
  jurisdiction?: string;
  department_path?: string[];
  current?: boolean;
  filterable?: boolean;
};

export type ScopeHierarchy = {
  state: "COMPLETE" | "CURRENT_ONLY" | "TRUNCATED" | "UNAVAILABLE";
  root: ScopeNode;
  current: ScopeNode;
  legal_entities: ScopeNode[];
  organization_scopes?: ScopeNode[];
  organization_scopes_truncated?: boolean;
};

export type OrganizationScopeSearchPage = {
  items: ScopeNode[];
  has_more: boolean;
};

export type RuntimeContext = {
  tenant: { id: string; name: string };
  legal_entity: { id: string; name: string };
  scope_hierarchy?: ScopeHierarchy;
  actor: {
    id: string;
    name: string;
    kind?: string;
    assurance_level?: string;
    authentication?: string;
    session_id?: string;
    role_codes?: string[];
    department_grants?: DepartmentGrant[];
  };
  mode: string;
  capabilities?: {
    config_read?: boolean;
    config_write?: boolean;
    identity_read?: boolean;
    identity_configure?: boolean;
    platform_operations_read?: boolean;
    platform_operations_write?: boolean;
    [key: string]: boolean | undefined;
  };
};

export type DemoAccount = {
  label: string;
  username: string;
  password: string;
  role_codes: string[];
};

export type SessionStatus = {
  authenticated: boolean;
  demo_login_available: boolean;
};

export type TodaySnapshot = { items: AttentionItem[]; generated_at?: string; count_basis?: "LOADED"; workflow_has_more?: boolean };
export type EmployeeProfile = { person: { id: string; display_name: string; status: string; position?: string; function?: string; sample: boolean }; metrics: Record<string, { value?: number }>; as_of: string };
export type EmployeeWorkItem = { id: string; record_type: string; record_id: string; responsibility: string; title: string; status: string; due_at?: string; updated_at: string };
export type EmployeeActivityItem = { event_id: string; occurred_at: string; action: string; record_type: string; record_id: string; title: string; source: string };
export type AuthorityResolveInput = {
  object_type: "PROGRAM" | "MATTER" | "EVIDENCE_REQUEST" | "THIRD_PARTY_RELATIONSHIP";
  object_id: string;
  responsibility: string;
  decision_type?: string;
  materiality: number;
};

let runtimeContext: Promise<RuntimeContext> | undefined;

function request<T>(path: string, init?: RequestInit): Promise<T> {
  return requestJSON<T>(apiBase, path, init);
}

export function loadContext(): Promise<RuntimeContext> {
  runtimeContext ??= request<RuntimeContext>("/api/v1/context").catch((error) => {
    runtimeContext = undefined;
    throw error;
  });
  return runtimeContext;
}

export function searchOrganizationScopes(query: string, limit = 30): Promise<OrganizationScopeSearchPage> {
  const value = query.trim();
  if (value.length < 2) return Promise.resolve({ items: [], has_more: false });
  const params = new URLSearchParams({ q: value, limit: String(Math.min(Math.max(limit, 1), 50)) });
  return request<OrganizationScopeSearchPage>(`/api/v1/context/organization-scopes?${params.toString()}`);
}

export function loadSessionStatus(): Promise<SessionStatus> {
  return request<SessionStatus>("/api/v1/session/status");
}

export async function switchLegalEntity(legalEntityID: string): Promise<void> {
  const value = legalEntityID.trim();
  if (!value) throw new Error("legal entity is required");
  await requestVoid(apiBase, "/auth/scope", { method: "POST", body: JSON.stringify({ legal_entity_id: value }) });
  runtimeContext = undefined;
}

export async function loadDemoAccounts(): Promise<DemoAccount[]> {
  return (await request<{ accounts: DemoAccount[] }>("/api/v1/demo/accounts")).accounts;
}

export async function loginDemo(username: string, password: string): Promise<void> {
  await request<{ account: DemoAccount }>("/api/v1/demo/login", { method: "POST", body: JSON.stringify({ username, password }) });
  runtimeContext = undefined;
}

export async function logoutDemo(): Promise<void> {
  await requestVoid(apiBase, "/api/v1/demo/logout", { method: "POST", body: "{}" });
  runtimeContext = undefined;
}

async function scopedPath(path: string, values: Record<string, string | number | undefined> = {}) {
  const context = await loadContext();
  const params = new URLSearchParams({ tenant_id: context.tenant.id });
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  return `${path}?${params.toString()}`;
}

async function scopedRequest<T>(path: string, values: Record<string, string | number | undefined> = {}, init?: RequestInit): Promise<T> {
  return request<T>(await scopedPath(path, values), init);
}

function summaryValues(query: SummaryQuery) {
  return {
    q: query.q,
    status: query.status,
    program_id: query.programID,
    overall_state: query.overallState,
    jurisdiction: query.jurisdiction,
    organization_scope_id: query.organizationScopeID,
    matter_type: query.matterType,
    due: query.dueCondition,
    priority: query.priority,
    assigned_to_me: query.assignedToMe ? "true" : undefined,
    cursor: query.cursor,
    limit: query.limit ?? 20,
  };
}

export function loadBankJourneys(): Promise<BankJourneysResponse> {
  return request<BankJourneysResponse>("/api/v1/bank-journeys");
}

export function loadToday(): Promise<TodaySnapshot> {
  return request<TodaySnapshot>("/api/v1/today");
}

export type ActorWorkScope = { tenantID: string; legalEntityID: string; principalID: string };

type ActorWorkReceipt = {
  as_of: string;
  coverage: "WORKFLOW_ASSIGNMENTS_ONLY";
  scope: { tenant_id: string; legal_entity_id: string; principal_id: string };
};

export type ActorWorkPage = ActorWorkReceipt & {
  items: AttentionItem[];
  next_cursor?: string;
  has_more: boolean;
  count_basis: "NOT_AVAILABLE";
};

export type ActorWorkDueCounts = ActorWorkReceipt & {
  total: number;
  overdue: number;
  due_soon: number;
  undated: number;
  count_basis: "SOURCE_SNAPSHOT";
};

export function loadActorWorkDueCounts(): Promise<ActorWorkDueCounts> {
  return scopedRequest<ActorWorkDueCounts>("/api/v1/work/assignment-counts");
}

export type ActorWorkFilters = {
  status?: "READY" | "IN_PROGRESS" | "BLOCKED" | "ESCALATED";
  due_from?: string;
  due_before?: string;
};

export function loadActorWorkPage(search: string, cursor?: string, filters: ActorWorkFilters = {}): Promise<ActorWorkPage> {
  return scopedRequest<ActorWorkPage>("/api/v1/work/assignments", {
    q: search.trim(),
    cursor,
    limit: 50,
    ...filters,
  });
}

export function loadEmployeeProfile(personID: string): Promise<EmployeeProfile> { return scopedRequest<EmployeeProfile>(`/api/v1/people/${encodeURIComponent(personID)}`); }
export function loadEmployeeWork(personID: string): Promise<{ items: EmployeeWorkItem[]; as_of: string }> { return scopedRequest(`/api/v1/people/${encodeURIComponent(personID)}/work`, { limit: 50 }); }
export function loadEmployeeActivity(personID: string): Promise<{ items: EmployeeActivityItem[]; as_of: string }> { return scopedRequest(`/api/v1/people/${encodeURIComponent(personID)}/activity`, { limit: 50 }); }

export async function resolveAuthority(input: AuthorityResolveInput): Promise<AuthorityResolution> {
  const context = await loadContext();
  return request<AuthorityResolution>("/api/v1/authority/resolve", {
    method: "POST",
    body: JSON.stringify({
      tenant_id: context.tenant.id,
      legal_entity_id: context.legal_entity.id,
      object_type: input.object_type,
      object_id: input.object_id,
      responsibility: input.responsibility,
      decision_type: input.decision_type,
      materiality: input.materiality,
    }),
  });
}

export async function loadCaptureRequest(): Promise<CaptureRequest> {
  const requestID = await sampleEvidenceRequestID();
  if (!requestID) throw new Error("No seeded evidence request is available for this role.");
  return loadEvidenceRequest(requestID);
}

export async function submitCaptureRequest(id: string, version: number, answers: Record<string, string>) {
  const context = await loadContext();
  return request<{ request_id: string; status: string; submitted_at: string }>(`/api/v1/evidence/requests/${encodeURIComponent(id)}/submissions?tenant_id=${encodeURIComponent(context.tenant.id)}`, { method: "POST", body: JSON.stringify({ tenant_id: context.tenant.id, expected_version: version, answers }) });
}

export function loadReadiness(): Promise<Readiness> {
  return scopedRequest<Readiness>("/api/v1/compliance/readiness");
}

export async function loadAutomationPolicyPage(signal?: AbortSignal): Promise<AutomationPolicyPage> {
  return request<AutomationPolicyPage>("/api/v1/compliance/automation-policies", { signal });
}

export async function loadAIGovernancePolicies(): Promise<AIGovernancePolicy[]> {
  return (await request<{ items: AIGovernancePolicy[] }>("/api/v1/ai-governance/policies?limit=20")).items;
}

export async function loadAIGovernanceWorkloads(): Promise<AIGovernanceWorkload[]> {
  return (await request<{ items: AIGovernanceWorkload[] }>("/api/v1/ai-governance/workloads?limit=20")).items;
}

export async function loadIntegrity(): Promise<IntegrityFinding[]> {
  return (await scopedRequest<{ findings: IntegrityFinding[] }>("/api/v1/authority/integrity")).findings;
}

export async function loadPolicies(): Promise<PolicySummary[]> {
  return (await scopedRequest<{ items: PolicySummary[] }>("/api/v1/authority/policies")).items;
}

export async function loadWorkflowTasks(): Promise<WorkflowTask[]> {
  return (await scopedRequest<{ items: WorkflowTask[] }>("/api/v1/workflow/tasks", { limit: 20 })).items;
}

export async function loadEvidenceSources(legalEntityID?: string): Promise<EvidenceSource[]> {
  return (await scopedRequest<{ items: EvidenceSource[] }>("/api/v1/evidence/sources", { legal_entity_id: legalEntityID, limit: 50 })).items;
}

export async function loadEvidenceRequests(): Promise<EvidenceRequest[]> {
  return (await scopedRequest<{ items: EvidenceRequest[] }>("/api/v1/evidence/requests", { limit: 50 })).items;
}

export type EvidenceRequestLoadIntent = "eligibility_preload" | "capture_revalidation";

export async function loadEvidenceRequest(id: string, intent?: EvidenceRequestLoadIntent): Promise<EvidenceRequest> {
  return scopedRequest<EvidenceRequest>(`/api/v1/evidence/requests/${encodeURIComponent(id)}`, { request_intent: intent });
}

export async function loadEvidenceReviewSubmission(id: string): Promise<EvidenceReviewSubmission> {
  return scopedRequest<EvidenceReviewSubmission>(`/api/v1/evidence/requests/${encodeURIComponent(id)}/review-submission`);
}

export async function loadProjectionHealth(): Promise<ProjectionHealth[]> {
  return (await scopedRequest<{ items: ProjectionHealth[] }>("/api/v1/operations/projections")).items;
}

export async function reconcileProgramState(): Promise<ReconcileResult> {
  const context = await loadContext();
  return request<ReconcileResult>("/api/v1/operations/projections/reconcile", { method: "POST", body: JSON.stringify({ tenant_id: context.tenant.id, limit: 250 }) });
}

export function loadBackgroundJobs(): Promise<BackgroundJobSnapshot> {
  return scopedRequest<BackgroundJobSnapshot>("/api/v1/operations/background-jobs", { limit: 100 });
}

export async function retryBackgroundJob(jobID: string, queue: string, expectedAttempts: number, rationale: string): Promise<JobRecoveryReceipt> {
  const context = await loadContext();
  return request<JobRecoveryReceipt>(`/api/v1/operations/background-jobs/${encodeURIComponent(jobID)}/retry`, { method: "POST", body: JSON.stringify({ tenant_id: context.tenant.id, queue, expected_attempts: expectedAttempts, rationale }) });
}

export function loadProgramSummaries(query: SummaryQuery = {}): Promise<SummaryPage<ProgramSummary>> {
  return scopedRequest<SummaryPage<ProgramSummary>>("/api/v1/program-summaries", summaryValues(query));
}

export function loadMatterSummaries(query: SummaryQuery = {}): Promise<SummaryPage<MatterSummary>> {
  return scopedRequest<SummaryPage<MatterSummary>>("/api/v1/matter-summaries", summaryValues(query));
}

export function loadProgram(id: string): Promise<ProgramAggregate> {
  return scopedRequest<Parameters<typeof normalizeProgramAggregate>[0]>(`/api/v1/programs/${encodeURIComponent(id)}`).then(normalizeProgramAggregate);
}

export function loadMatter(id: string): Promise<MatterAggregate> {
  return scopedRequest<NullableMatterAggregate>(`/api/v1/matters/${encodeURIComponent(id)}`).then(normalizeMatterAggregate);
}

export function loadProgramAt(id: string, at: string): Promise<ProgramAggregate> {
  return scopedRequest<Parameters<typeof normalizeProgramAggregate>[0]>(`/api/v1/programs/${encodeURIComponent(id)}/history`, { at }).then(normalizeProgramAggregate);
}

export function loadMatterAt(id: string, at: string): Promise<MatterAggregate> {
  return scopedRequest<NullableMatterAggregate>(`/api/v1/matters/${encodeURIComponent(id)}/history`, { at }).then(normalizeMatterAggregate);
}

export function loadResponsePackageHistory(matterID: string, responseID: string, limit = 20): Promise<ResponseHistoryPage> {
  return scopedRequest<ResponseHistoryPage>(`/api/v1/matters/${encodeURIComponent(matterID)}/responses/${encodeURIComponent(responseID)}/history`, { limit });
}

export async function loadPrograms(): Promise<ProgramAggregate[]> {
  return (await scopedRequest<{ items: Parameters<typeof normalizeProgramAggregate>[0][] }>("/api/v1/programs", { limit: 50 })).items.map(normalizeProgramAggregate);
}

export async function loadMatters(status = "OPEN"): Promise<MatterAggregate[]> {
  return (await scopedRequest<{ items: NullableMatterAggregate[] }>("/api/v1/matters", { limit: 50, status })).items.map(normalizeMatterAggregate);
}

async function sampleEvidenceRequestID() {
  const journeys = await loadBankJourneys().catch(() => null);
  return journeys?.items.find((journey) => journey.action_target_type === "EVIDENCE_REQUEST" && journey.action_target_id)?.action_target_id
    ?? journeys?.items.find((journey) => journey.evidence_request_id)?.evidence_request_id
    ?? "";
}

type NullableMatterAggregate = Omit<MatterAggregate, "links" | "decisions" | "actions" | "verification_contracts" | "verification_results" | "response_packages" | "closure" | "matter"> & {
  matter: MatterAggregate["matter"] & { known_facts?: Record<string, unknown> | null; missing_facts?: unknown[] | null; contradictions?: unknown[] | null };
  links?: MatterAggregate["links"] | null;
  decisions?: MatterAggregate["decisions"] | null;
  actions?: MatterAggregate["actions"] | null;
  verification_contracts?: MatterAggregate["verification_contracts"] | null;
  verification_results?: MatterAggregate["verification_results"] | null;
  response_packages?: MatterAggregate["response_packages"] | null;
  closure?: MatterAggregate["closure"] | null;
};

function arrayOrEmpty<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function normalizeMatterAggregate(value: NullableMatterAggregate): MatterAggregate {
  return {
    ...value,
    matter: {
      ...value.matter,
      known_facts: value.matter.known_facts ?? {},
      missing_facts: arrayOrEmpty(value.matter.missing_facts),
      contradictions: arrayOrEmpty(value.matter.contradictions),
    },
    links: arrayOrEmpty(value.links),
    decisions: arrayOrEmpty(value.decisions),
    actions: arrayOrEmpty(value.actions),
    verification_contracts: arrayOrEmpty(value.verification_contracts),
    verification_results: arrayOrEmpty(value.verification_results),
    response_packages: arrayOrEmpty(value.response_packages),
    closure: { ready: value.closure?.ready ?? false, reasons: arrayOrEmpty(value.closure?.reasons) },
  };
}
